import React, { useCallback } from "react";
import { XMarkIcon } from "@";

/**
 * A modal component that wraps the children with a modal container.
 * @param {string} title The title of the modal
 * @param {string} size The size of the modal, can be "xs", "sm", "md", "lg", "xl", or "2xl"
 * @param {function} close A function to call when the modal is closed
 * @param {React.ReactNode} children The content of the modal
 * @returns {React.ReactNode} The modal component
 */
const Modal = React.memo(
  ({
    title,
    size,
    close,
    children,
    id = "main-modal",
    fullHeight,
    centered = true,
  }) => {
    const handleClose = useCallback(() => {
      close();
    }, [close]);

    return (
      <>
        <div
          id={id}
          tabIndex="50"
          aria-hidden="true"
          // data-modal-backdrop="static"
          className={`fixed end-0 start-0 top-0 z-50 mt-1 hidden h-[calc(100%-1rem)] max-h-full w-full overflow-scroll overflow-y-auto overflow-x-hidden md:inset-0 md:mt-5 ${
            centered
              ? "items-center justify-center"
              : "items-start justify-start"
          }`}
        >
          <div
            className={`relative z-20 overflow-visible px-1 md:px-4 ${
              centered ? "h-auto w-full" : "h-full w-full md:h-auto"
            } ${size ? size : "max-w-2xl"}`}
          >
            <div className="relative overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl shadow-gray-900/10 dark:border-gray-700/60 dark:bg-gray-800 dark:shadow-black/40">
              {(title || close) && (
                <div className="relative flex items-center justify-between gap-3 border-b border-gray-100 bg-gradient-to-l from-purple-50/40 via-white to-transparent px-4 py-3 dark:border-gray-700/60 dark:from-purple-950/30 dark:via-gray-800 dark:to-transparent md:px-6">
                  {/* Subtle hairline accent */}
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-purple-200/50 to-transparent dark:via-purple-800/40"
                  />

                  {title && (
                    <div className="flex items-center gap-3">
                      <span className="hidden h-6 w-1 rounded-full bg-gradient-to-b from-purple-700 to-purple-950 dark:from-purple-400 dark:to-purple-700 sm:block" />
                      <h1 className="text-lg font-bold tracking-tight text-gray-900 dark:text-gray-100">
                        {title}
                      </h1>
                    </div>
                  )}
                  {close && (
                    <button
                      type="button"
                      onClick={handleClose}
                      data-modal-toggle={id}
                      aria-label="إغلاق"
                      className="ms-auto inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-gray-100 bg-white text-gray-500 transition-all duration-200 hover:rotate-90 hover:border-red-200 hover:bg-red-50 hover:text-red-500 active:scale-95 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:border-red-900/40 dark:hover:bg-red-950/30 dark:hover:text-red-400"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              )}
              <div
                className={`relative ${
                  fullHeight
                    ? "overflow-visible"
                    : "max-h-[85vh] overflow-y-scroll lg:max-h-[80vh]"
                }`}
              >
                {children}
              </div>
            </div>
          </div>
          <div className="fixed inset-0 z-10 bg-gray-900/60 backdrop-blur-sm dark:bg-black/20" />
        </div>
      </>
    );
  },
);

export default Modal;
